import { useNavigate } from 'react-router-dom';
import React, { useCallback, useMemo, useRef } from 'react';

import Tippy from '@tippyjs/react';

import { createServiceCategory, deleteServiceCategory } from 'api/ServiceCategory';

import useModal from 'lib/hooks/useModal';
import { iaRa, optionify } from 'lib/helpers/utility';
import { useMember } from 'lib/hooks/queries/member/useMember';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useServiceCategories } from 'lib/hooks/queries/services/useServiceCategories';

import Select from 'components/shared/Select/Select';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import Textarea from 'components/shared/Textarea/Textarea';
import Allowed from 'components/shared/Permissions/Allowed';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

import Wrapper from '../Wrapper/Wrapper';
import {
  CATEGORY_CREATED_SUCCESS_MESSAGE,
  CATEGORY_DELETED_ERROR_MESSAGE,
  CATEGORY_DELETED_SUCCESS_MESSAGE,
  SELF_SCHEDULING_TIME_LENGTH_ERROR_MESSAGE
} from '../../lib/constants';

import CategoryOption from './components/CategoryOption';

const CATEGORY_QUERY_KEY = 'serviceCategories';

const DetailsSection = ({ formik }) => {
  const { data: member = [] } = useMember();
  const practitioners = useMemo(() => {
    return (
      member?.members?.map((practitioner) => ({
        value: practitioner.user_id,
        label: `${practitioner.f_name} ${practitioner.l_name}`
      })) || []
    );
  }, [member?.members]);

  const descriptionRef = useRef();

  // Self-scheduling requires a bookable duration, so gate the toggle on time_length > 0.
  const hasValidTimeLength = Number(formik.values?.time_length) > 0;
  // Only block enabling; keep an already-checked box interactive so the user can turn it off.
  const isSelfSchedulingLocked = !hasValidTimeLength && !formik.values?.self_scheduling;

  const { isOpen: showDeleteCategory, openModal, closeModal } = useModal();

  const navigate = useNavigate();

  const { data } = useServiceCategories({ options: { enabled: !!formik.values?.self_scheduling } });
  const categories = data?.categories || [];

  const { mutateAsync: deleteCategory } = useCustomMutation({
    mutationFn: () => deleteServiceCategory(navigate, { categoryId: showDeleteCategory?.value }),
    invalidateQueryKey: CATEGORY_QUERY_KEY,
    successMessage: CATEGORY_DELETED_SUCCESS_MESSAGE,
    errorMessage: CATEGORY_DELETED_ERROR_MESSAGE,
    onSuccess: () => {
      closeModal();
    }
  });

  const handleCreateCategory = async (newCategory) => {
    const res = await createServiceCategory(navigate, { category: { name: newCategory } });

    if (res?.code === 0) {
      formik.setFieldValue('category_id', res?.category?.id || null);
    }
    return res;
  };

  const { mutateAsync: createCategory } = useCustomMutation({
    mutationFn: (newCategory) => handleCreateCategory(newCategory),
    invalidateQueryKey: CATEGORY_QUERY_KEY,
    successMessage: CATEGORY_CREATED_SUCCESS_MESSAGE,
    onSuccess: () => {
      closeModal();
    }
  });

  const selectedPractitioners = (practitioners) => {
    const result = practitioners?.map((practitioner) => {
      return practitioner?.value;
    });
    formik.setFieldTouched('practitioner_ids', true, false);
    formik.setFieldValue('practitioner_ids', iaRa(result));
  };

  const practitionerNames = practitioners
    ?.map((practitioner) => {
      if (formik.values.practitioner_ids?.includes(practitioner.value)) {
        return practitioner;
      }
    })
    .filter(Boolean);

  const Option = useCallback(
    (props) => <CategoryOption {...props} onDelete={openModal} />,
    [openModal]
  );

  return (
    <>
      {/* Description - Full width */}
      <Wrapper isColumn>
        <Textarea
          label="Description"
          value={formik.values.description}
          onChange={formik.handleChange}
          isOptional
          onBlur={formik.handleBlur}
          name="description"
          id="serviceDescriptionTextarea"
          data-qa="description-input"
          textareaClassName="min-h-[100px]"
          maxLength={200}
          resizeY
          error={formik.touched.description && formik.errors.description}
          forwardedRef={descriptionRef}
          placeholder="Enter description"
        />
      </Wrapper>

      {/* Divider */}
      <div className="h-px w-full bg-neutral-200" />

      {/* Self Scheduling Section */}
      <Allowed requiredModules="core.calendar_scheduling.appointments">
        <div className="flex flex-col gap-4 pb-5">
          <div className="flex flex-col gap-1">
            <span className="text-base font-semibold text-neutral-900">Self-scheduling</span>
            <span className="text-sm text-neutral-600">
              Enable this service for patient self-scheduling and select the available providers and
              category.
            </span>
          </div>

          <Tippy
            className="tippy-dark"
            placement="top-start"
            disabled={!isSelfSchedulingLocked}
            content="Set a time length greater than 0 minutes to enable self-scheduling.">
            <div className="w-fit">
              <Checkbox
                label="Display service in patient self-scheduling"
                onChange={(event) => formik.setFieldValue('self_scheduling', event.target.checked)}
                checked={formik.values?.self_scheduling}
                disabled={isSelfSchedulingLocked}
                id="serviceSelfSchedulingSwitch"
                data-qa="service-self-scheduling-switch"
              />
            </div>
          </Tippy>

          {formik.values?.self_scheduling && !hasValidTimeLength && (
            <span className="text-xs text-danger-500" data-qa="self-scheduling-time-length-error">
              {SELF_SCHEDULING_TIME_LENGTH_ERROR_MESSAGE}
            </span>
          )}

          {formik.values?.self_scheduling && (
            <div className="flex items-start gap-4">
              <Select
                label="Providers available for this service"
                placeholder="Select available providers"
                parentClassName="flex flex-1 flex-col"
                isMulti
                required
                value={practitionerNames}
                name="practitioner_ids"
                onChange={selectedPractitioners}
                options={practitioners}
                id="servicePractitionerSelect"
                data-qa="practitioners-available-for-this-service"
                menuPortalTarget={document.body}
                error={formik.touched.practitioner_ids && formik.errors.practitioner_ids}
              />
              <Select
                label="Category"
                isCreatable={true}
                onChange={(e) => formik.setFieldValue('category_id', e?.value || null)}
                value={
                  formik.values.category_id && {
                    label: categories?.find((item) => item.id === formik.values.category_id)?.name,
                    value: formik.values.category_id
                  }
                }
                options={optionify(categories, 'name', 'id')}
                placeholder="Type and select category"
                noOptionsMessage={() => 'Start typing to create a category'}
                error={formik.touched.category_id && formik.errors.category_id}
                id="serviceCategory"
                parentClassName="flex flex-1 flex-col"
                onCreateOption={(e) => createCategory(e)}
                isClearable={true}
                components={{ Option, IndicatorSeparator: () => null }}
                menuPortalTarget={document.body}
              />
            </div>
          )}
        </div>
      </Allowed>

      {showDeleteCategory && (
        <Confirm
          handleOpen={showDeleteCategory?.value}
          handleClose={closeModal}
          title="Delete category"
          message={`Are you sure you want to delete "${showDeleteCategory?.label}" category?`}
          variant="danger"
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: deleteCategory,
              color: 'danger'
            }
          }}
        />
      )}
    </>
  );
};

export default DetailsSection;
